import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnInit, Optional, ViewChild, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { IProductDataV3 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { ISizeName } from '@df/core/service/client';
import { StylesService } from '@df/core/service/styles';
import { Status } from '@df/core/status';
import { ImageModule } from '@df/module-image/image.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import compact from 'lodash-es/compact';
import groupBy from 'lodash-es/groupBy';
import range from 'lodash-es/range';
import uniqBy from 'lodash-es/uniqBy';
import { ActionComponent } from 'projects/app-default/src/app/ui/api/action.component';
import { ResultComponent } from 'projects/app-default/src/app/ui/api/result.component';
import { InputWrapBodyDirective } from 'projects/app-default/src/app/ui/form/input-wrap-body.directive';
import { InputWrapComponent } from 'projects/app-default/src/app/ui/form/input-wrap.component';
import { NModalLayoutRight } from '../../../../../app/ui/modal/modal-layout-right.component';
import { Product } from '../../product.decorator';
import { Sample } from './../../../../module/samples/sample';
import { Samples } from './../../../../module/samples/samples';
import { ProductViewSamplesModalItemComponent } from './item/product-view-samples-modal-item.component';
export const PRODUCT_VIEW_SAMPLES_MODAL_CMS_ID = 'product-view.samples.modal';
export interface IProductViewSamplesModalItem {
    title: string;
    products: IProductViewSamplesModalProduct[];
}
export interface IProductViewSamplesModalProduct {
    product: Product<IProductDataV3>;
    sampleProduct: Product<IProductDataV3>;
}

export interface ProductViewSamplesModalContent {
    title: string;
    text?: string;
}
@DfCmsModal({
    id: PRODUCT_VIEW_SAMPLES_MODAL_CMS_ID,
    name: 'Product View Samples Modal',
    position: (sizeId: ISizeName) => {
        return sizeId === 'S' ? 'bottom' : 'right';
    },
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        width: NModalLayoutRight.getWidth(sizeName),
        position: sizeName === 'S' ? { bottom: '0' } : NModalLayoutRight.ModalConfig.position,
        height: { S: StylesService.vh(85), M: StylesService.vh(100), L: StylesService.vh(100), X: StylesService.vh(100) }[sizeName]
    }),
    reopenPolicy: NDfModal.ReopenPolicy.first,
    prerenderAllowed: false,
    sizeRestriction: ['S', 'M', 'L', 'X']
})
@ICmsComponent<ProductViewSamplesModalComponent>({
    id: PRODUCT_VIEW_SAMPLES_MODAL_CMS_ID,
    block: {
        url: PRODUCT_VIEW_SAMPLES_MODAL_CMS_ID
    },
    onDemandOnly: true,
    name: 'Product View Samples Modal',
    description: 'Product View Samples Modal Content',
    fields: [
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'text',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Text'
        }
    ]
})
@Component({
    selector: 'product-view-samples-modal',
    templateUrl: './product-view-samples-modal.html',
    styleUrls: ['./product-view-samples-modal.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        InputWrapComponent,
        ResultComponent,
        TranslationModule,
        ActionComponent,
        GridModule,
        ProductViewSamplesModalItemComponent,
        ImageModule,
        IfSizeDirective,
        SizeClassDirective,
        InputWrapBodyDirective
    ]
})
export class ProductViewSamplesModalComponent
    extends UiComponent
    implements NICmsContentComponent.IComponent, OnInit, AfterViewInit
{
    @ICmsContent({
        type: 'blockContent'
    })
    content?: ProductViewSamplesModalContent;
    valid = '';
    private samples = inject(Samples);
    @ViewChild('form', { static: false })
    form?: NgForm;
    items?: IProductViewSamplesModalItem[];
    slots = range(this.limit || 0);

    constructor(
        private app: App,
        @Optional()
        @Inject(MODAL_DATA)
        private data: Product<IProductDataV3>,
        @Optional()
        private modalRef: ModalRef<ProductViewSamplesModalComponent>
    ) {
        super();
    }
    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(
            this.samples.updateSubject.subscribe(() => {
                this.valid = this.selectedSamples?.length ? 'selected' : '';
                this.form?.ngSubmit.emit();
                this.detectChanges();
                this.timeoutService.setTimeout(() => {
                    this.changeDetectorRef.detectChanges();
                });
            })
        );
        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
        this.samples.reset();
        const sampleFabricAttribute = this.app.resources.attribute.get('sample_fabric');
        this.items = compact(
            Object.values(
                groupBy(
                    uniqBy(
                        this.product.family
                            .flatMap(i => {
                                const product = i as Product<IProductDataV3>;
                                if (product) {
                                    const sampleProducts = product.sampleProducts;
                                    if (sampleProducts) {
                                        return sampleProducts.map(sampleProduct => {
                                            if (sampleProduct) {
                                                return {
                                                    product,
                                                    sampleProduct
                                                };
                                            }
                                        });
                                    }
                                }
                                return [];
                            })
                            .filter(i => !!i),
                        i => i?.sampleProduct.id
                    ),
                    i => i?.sampleProduct.data?.sample_fabric
                )
            ).map(i => {
                const title =
                    i[0]?.sampleProduct.data?.sample_fabric &&
                    (sampleFabricAttribute?.getOptionLabel(i[0].sampleProduct.data.sample_fabric) as string);

                return (
                    title && {
                        title,
                        products: compact(i)
                    }
                );
            })
        );
        this.detectChanges();
    }
    ngAfterViewInit(): void {
        this.subscriptions.add(this.form?.ngSubmit.subscribe(() => this.detectChanges()));
    }
    get selectedSamples(): Sample[] {
        return this.samples.items;
    }
    get limit(): number | undefined {
        return this.samples.limit;
    }
    get product(): Product<IProductDataV3> {
        return this.data;
    }
    get status(): Status {
        return this.samples.status;
    }
    isNextSlot(index: number): boolean {
        return this.selectedSamples.length === index;
    }
    submit(): void {
        this.app.request.submitForm(this.form, undefined, () => this.samples.submit());
    }
    trackBySampleFabric(index: number, item: IProductViewSamplesModalItem): string {
        return item.title;
    }
    trackBySampleId(index: number, product: IProductViewSamplesModalProduct): number {
        return product.sampleProduct.id;
    }
    close(): void {
        this.modalRef?.close();
    }
}
